// A clip's speed in the Parameters (spec speed-section, D6): "Speed" after Trim for a video or a
// sound. Its tabs, Normal and Curve (spec clip-speed-curves-tab, layout A). Normal: a slider from
// 0.25× to 4× by halvings, so 1× sits in its middle, committed when let go; a box typed in
// hundredths; live's MCP presets and 1× as chips. Curve: the eight tiles and the curve's editor.
// Under both, the length; and "Ripple", on as live's MCP has it, and on and fixed on the main track,
// whose magnet moves the later clips either way (spec magnet-ripple).
import { Button, Group, NumberInput, SegmentedControl, Slider, Switch, Text } from "@mantine/core";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import type { SpeedCurveName } from "../../api/generated/model";
import { CurveEditor } from "./CurveEditor";
import { CurveTiles } from "./CurveTiles";
import { ParamSection } from "./params";
import type { Playhead } from "./playhead";
import { CURVE_PRESETS, CUSTOM_START, curved, pointsOver, type CurvePoint } from "./speed-curve";
import {
  MAX_SPEED,
  MIN_SPEED,
  REFUSALS,
  clipMs,
  clipsOn,
  magnetic,
  refusal,
  spanOf,
  type AudioClip,
  type Timeline,
  type TimelineEdit,
  type VideoClip,
} from "./timeline";
import classes from "./editor.module.css";

/** A speed as the editor writes it: "2×", "0.5×", "1.25×". */
export function speedText(speed: number): string {
  return `${Number(speed.toFixed(2))}×`;
}

/** Live's MCP's presets, and 1×. */
const PRESETS = [0.5, 1, 2, 4] as const;
/** The slider's place of a speed, by halvings: 0.25× at 0, 1× at 0.5, 4× at 1. */
const placeOf = (speed: number) => (Math.log2(speed) + 2) / 4;
const speedAt = (place: number) => Math.round(2 ** (place * 4 - 2) * 100) / 100;
const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;

/** The words for a speed refused without Ripple because the clip would run into the next (D6). */
export const RUNS_INTO = "It would run into the next clip: turn on Ripple, or make room.";
/** Why Ripple is on and fixed on the main track: its magnet packs it after every edit. */
export const MAGNET_RIPPLES = "The main track magnet keeps its clips back to back: later clips move with it.";

type SpeedTab = "normal" | "curve";

export function SpeedSection(props: {
  clip: VideoClip | AudioClip;
  timeline: Timeline;
  /** The playhead's store: the curve's editor draws it, and Add point puts a point there. */
  playhead: Playhead;
  /** A count the phone's Speed action raises: each new one brings the section into view. */
  shown?: number | undefined;
  onEdit: (edit: TimelineEdit) => void;
}) {
  const { clip, timeline, playhead, onEdit } = props;
  const [chosen, setChosen] = useState(true);
  // The main track packs its clips whatever the edit asks, so there Ripple is on and fixed.
  const packed = magnetic(timeline, clip.trackId);
  const ripple = packed || chosen;
  const [said, setSaid] = useState<string | null>(null);
  const [dragged, setDragged] = useState<number | null>(null);
  const [revision, setRevision] = useState(0);
  // A clip opens on Curve while it plays one; a tab chosen is kept for its clip.
  const [tabs, setTabs] = useState<Readonly<Record<string, SpeedTab>>>({});
  const tab = tabs[clip.id] ?? (curved(clip) ? "curve" : "normal");
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (props.shown !== undefined && props.shown > 0) root.current?.scrollIntoView?.({ block: "start" });
  }, [props.shown]);
  const shown = dragged ?? clip.speed;
  const apply = (edit: TimelineEdit) => {
    const why = refusal(timeline, edit);
    const next = clipsOn(timeline, clip.trackId, clip.id).find((each) => each.startMs >= clip.endMs);
    setSaid(why === null ? null : next !== undefined && why === REFUSALS.overlap(clip.id, next.id) ? RUNS_INTO : why);
    if (why === null) onEdit(edit);
  };
  const set = (speed: number) => apply({ type: "setSpeed", id: clip.id, speed, ripple });
  /**
   * A curve laid over the span the clip reads, as one edit. None goes back to one speed at 1×, as its
   * tile says: the curve taken away, or another speed set to 1×.
   */
  const setCurve = (name: SpeedCurveName | null, shape: readonly CurvePoint[] | null) => {
    if (name === null || shape === null) {
      if (curved(clip)) apply({ type: "setSpeedCurve", id: clip.id, curve: null, ripple });
      else if (clip.speed !== 1) set(1);
      return;
    }
    apply({ type: "setSpeedCurve", id: clip.id, curve: { name, points: pointsOver(shape, clip.inMs, spanOf(clip)) }, ripple });
  };
  const curveName = curved(clip) ? clip.speedCurve : null;
  const commitTyped = (raw: string) => {
    const typed = Number(raw.replace(/[^\d.]/g, ""));
    if (raw.trim() !== "" && Number.isFinite(typed)) set(Math.min(MAX_SPEED, Math.max(MIN_SPEED, typed)));
    setRevision((current) => current + 1);
  };
  const off = clip.locked;
  const flat = !curved(clip);
  const normal = (
    <>
      <div className={classes.levelRow} data-bare>
        <Slider
          className={classes.levelSlider}
          min={0}
          max={1}
          step={0.01}
          value={placeOf(shown)}
          label={null}
          thumbLabel="Speed"
          thumbValueText={(place) => speedText(speedAt(place))}
          disabled={off}
          onChange={(place) => setDragged(speedAt(place))}
          onChangeEnd={(place) => {
            setDragged(null);
            set(speedAt(place));
          }}
        />
        {/* While the slider is dragged, the box shows the speed it would make. */}
        <NumberInput
          key={`${shown}:${revision}`}
          aria-label="Speed in hundredths"
          classNames={{ input: classes.lookInput }}
          defaultValue={shown}
          decimalScale={2}
          rightSection="×"
          clampBehavior="none"
          hideControls
          disabled={off}
          onBlur={(event) => commitTyped(event.currentTarget.value)}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") commitTyped(event.currentTarget.value);
          }}
        />
      </div>
      <Group gap={6}>
        {PRESETS.map((preset) => (
          <Button
            key={preset}
            size="compact-xs"
            variant={flat && clip.speed === preset ? "light" : "default"}
            aria-pressed={flat && clip.speed === preset}
            disabled={off}
            onClick={() => set(preset)}
          >
            {speedText(preset)}
          </Button>
        ))}
      </Group>
    </>
  );
  const curve = (
    <>
      <CurveTiles clip={clip} disabled={off} onPick={setCurve} />
      {curveName === null ? null : (
        <CurveEditor
          // Its own for each clip: a point chosen or being dragged is never another clip's.
          key={clip.id}
          clip={clip}
          playhead={playhead}
          disabled={off}
          onCurve={(points) => setCurve(curveName, points)}
          onReset={() => setCurve(curveName, curveName === "custom" ? CUSTOM_START : CURVE_PRESETS[curveName])}
        />
      )}
    </>
  );
  return (
    <div ref={root}>
      <ParamSection title="Speed">
      <SegmentedControl
        size="xs"
        fullWidth
        aria-label="Speed kind"
        value={tab}
        disabled={off}
        onChange={(next) => setTabs((current) => ({ ...current, [clip.id]: next === "curve" ? "curve" : "normal" }))}
        data={[
          { value: "normal", label: "Normal" },
          { value: "curve", label: "Curve" },
        ]}
      />
      {tab === "normal" ? normal : curve}
      <Text className={classes.paramNote}>
        Lasts {seconds(clipMs(clip))} ({seconds(clipMs(clip) * clip.speed)} at 1×)
      </Text>
      <Switch
        label="Ripple"
        labelPosition="left"
        classNames={{ body: classes.paramSwitch, label: classes.paramLabel }}
        checked={ripple}
        disabled={off || packed}
        onChange={(event) => setChosen(event.currentTarget.checked)}
      />
      <Text className={classes.paramNote}>
        {packed ? MAGNET_RIPPLES : ripple ? "Later clips on this track move with it." : "Later clips on this track stay."}
      </Text>
      {said === null ? null : (
        <Text className={classes.paramNote} role="alert">
          {said}
        </Text>
      )}
      </ParamSection>
    </div>
  );
}
